import React from 'react';
import { useSelector } from 'react-redux';
import userNotFoundImg from 'src/assets/user_not_found.png';
import { useAuth0 } from 'src/auth/useAuth0.hook';
import { RootState } from 'src/redux/config/rootReducer';
import { i18nService } from 'src/services/i18n.service';
import { graphQLRequest } from 'src/utils/graphQL/graphQLRequestModule';
import { logoutQuery } from 'src/utils/graphQL/queries/general/logoutQuery';
import { useGraphQLRequest } from 'src/utils/graphQL/useGraphQLRequest.hook';
import useLanguageDirection from 'src/utils/hooks/useLanguageDirection';
import './StyledUserNotFound.css';

/**
 * UserNotFound is a React functional component that renders an error message
 * indicating that a user was not found.
 * Note: There is no hebrew translation since the user has not been found and no language is set.
 */
const UserNotFound: React.FC = () => {
  // Use the useAuth0 hook to access the user's authentication information.
  const { user: authUser, getTokenSilently, logout } = useAuth0();

  //get user from redux
  const user = useSelector((state: RootState) => state.user.user);

  //get client from useGraphQLRequest hook
  const { client } = useGraphQLRequest();

  //get direction
  const direction = useLanguageDirection();

  //get email from authUser
  const email = authUser?.email;

  /**
   *  Use the useGraphQLRequest hook to access the GraphQL client.
   */
  const handleLogout = async () => {
    // If the user is not authenticated, return.
    if (!user) {
      logout();
      return;
    }

    //get token and send logout request
    const token = await getTokenSilently();
    const query = logoutQuery;
    const res = await graphQLRequest(token, client, query);

    // If the request fails, log an error message and return.
    if (!res)
      console.error(`Failed to logout user: ${user.email} , with the following error: ${res}`);

    // try to log out even if the request fails
    //logout user via auth0
    logout && logout();
  };

  /**
   * Message to display when the user's email is not found depending on the direction.
   * @returns a JSX element that displays a message indicating that the user's email was not found.
   */
  const EmailNotFoundMessage = () => {
    return direction === 'ltr' ? (
      <p className="not-found-message">
        {i18nService.translate('Sorry, We could not find')}{' '}
        <span className="not-found-email">{email ?? i18nService.translate('Unknown Email')}</span>{' '}
        {i18nService.translate('in our system')}
      </p>
    ) : (
      <p className="not-found-message">
        {i18nService.translate('in our system')}{' '}
        <span className="not-found-email">{email ?? i18nService.translate('Unknown Email')}</span>{' '}
        {i18nService.translate('Sorry, We could not find')}
      </p>
    );
  };

  return (
    <div className="not-found-container">
      <div className="not-found-illustration">
        <img src={userNotFoundImg} alt="User not found" />
      </div>
      <h1 className="not-found-title">
        <span>{i18nService.translate('Ooops!!')}</span> {i18nService.translate('User not found')}
      </h1>
      <EmailNotFoundMessage />
      <p className="not-found-instruction-manager">
        {i18nService.translate('To log in as a manager please')}{' '}
        <a
          href="https://achieved.io/operator"
          target="_blank"
          rel="noreferrer"
          className="not-found-manager-link"
        >
          {i18nService.translate('click here')}
        </a>
      </p>
      <p className="not-found-instruction">
        {i18nService.translate(
          'To log in as an investor please make sure to log-in using the correct email address',
        )}
      </p>
      <button className="not-found-btn" onClick={() => handleLogout()}>
        {i18nService.translate('Use A Different Email Address')}
      </button>
    </div>
  );
};

export default UserNotFound;
